import $ from "jquery";

interface Ranking {
  type: string;
  title: string;
  valueColumn: string;
  valueUnit: string;
  fixStatsPoint: boolean;
  floorStats: boolean;
}

type Entry = [number, string, number]; // [rank, name, stats]

class RankingTable {
  private static readonly DEFAULT_COLUMNS = ["位", "食材名"];
  private static readonly DEFAULT_TYPE = "si";

  protected keywordRankingRoot: JQuery<HTMLElement>;
  protected type: string;
  protected title: string;
  protected valueColumn: string;
  protected valueUnit: string;
  protected requireFixStatsPoint: boolean;
  protected requireFloorStats: boolean;
  protected columns: string[];
  protected isLastTable: boolean;
  protected entries: Entry[];
  protected queryParams: any;

  constructor(ranking: Ranking, keywordRankingRoot: JQuery<HTMLElement>) {
    this.keywordRankingRoot = keywordRankingRoot;
    this.type = ranking.type;
    this.title = ranking.title;
    this.valueColumn = ranking.valueColumn;
    this.valueUnit = ranking.valueUnit;
    this.requireFixStatsPoint = ranking.fixStatsPoint;
    this.requireFloorStats = ranking.floorStats;
    this.columns = RankingTable.DEFAULT_COLUMNS.concat([this.valueColumn]);
    this.isLastTable = false;
    this.entries = [];
  }

  tableClassName(): string {
    return `${this.type}_rank`;
  }

  setEntries(entries: Entry[]): void {
    this.entries = entries;
  }

  setIsLastTable(isLastTable: boolean): void {
    this.isLastTable = isLastTable;
  }

  setQueryParams(params: any): void {
    delete params.format;
    delete params.types;
    delete params.max_rank;
    if (params.area === 0 && params.category === 0) {
      delete params.dimension;
      delete params.area;
      delete params.category;
    }
    params.type = this.type === "ysi" ? RankingTable.DEFAULT_TYPE : this.type;
    this.queryParams = params;
  }

  toHtml(): string {
    if (this.entries.length > 0) {
      return this.tableElement();
    } else {
      return this.noEntriesParagraph();
    }
  }

  private noEntriesParagraph(): string {
    return `
      <div class="${this.tableDivClassName()}">
        <h4>${this.title}</h4>
        <p class="${this.tableClassName()}">データなし</p>
      </div>
    `;
  }

  private tableDivClassName(): string {
    return this.isLastTable ? "keyword_table last" : "keyword_table";
  }

  private tableElement(): string {
    return `
      <div class="${this.tableDivClassName()}">
        <h4>${this.title}</h4>
        <table class="${this.tableClassName()}">
          ${this.tableHeader()}
          ${this.tableBody()}
        </table>
      </div>
    `;
  }

  private tableHeader(): string {
    const headerColumns = this.columns
      .map((column) => `<th>${column}</th>`)
      .join("");
    return `<thead><tr>${headerColumns}</tr></thead>`;
  }

  protected tableBody(): string {
    const rows = this.entries.map((entry, index) => {
      const klass = index % 2 === 0 ? "odd" : "even";
      const [rank, name, stats] = entry;
      const formattedStats = this.requireFormatStats()
        ? this.formatStats(stats)
        : `${stats}${this.valueUnit}`;
      return `
        <tr class="${klass}">
          <td class="rank">${this.rankingNumber(rank)}</td>
          <td>${this.hyperLinkFor(name)}</td>
          <td>${formattedStats}</td>
        </tr>
      `;
    });
    return `<tbody>${rows.join("")}</tbody>`;
  }

  protected rankingImagePaths(): string[] {
    return this.keywordRankingRoot.data("ranking_image_paths");
  }

  protected rankingImageFor(rank: number): string {
    return this.rankingImagePaths()[rank - 1];
  }

  protected destinationPath(): string {
    return this.keywordRankingRoot.data("dest_path");
  }

  protected hyperLinkFor(itemName: string): string {
    const urlParams = $.param(this.queryParams);
    return `<a href="${this.destinationPath()}/${itemName}?${urlParams}">${itemName}</a>`;
  }

  protected requireFormatStats(): boolean {
    return this.requireFixStatsPoint || this.requireFloorStats;
  }

  protected formatStats(stats: number | null): string {
    if (!stats) {
      return "-";
    }
    if (this.requireFixStatsPoint) {
      return `${stats.toFixed(this.decimalPoint(stats))}${this.valueUnit}`;
    }
    if (this.requireFloorStats) {
      if (stats < 0.01) {
        return `0.01${this.valueUnit}未満`;
      }
      if (stats < 1) {
        return `${stats.toFixed(2)}${this.valueUnit}`;
      }
      return `${Math.floor(stats)}${this.valueUnit}`;
    }
    return `${stats}${this.valueUnit}`;
  }

  protected rankingNumber(rank: number): string {
    const rankNum = parseInt(rank.toString(), 10);
    if (rankNum <= 3) {
      return `<img src="${this.rankingImageFor(rankNum)}" />`;
    } else {
      return rankNum.toString();
    }
  }

  protected decimalPoint(stats: number): number {
    if (stats > 1) return 1;
    if (stats > 0.1) return 2;
    if (stats > 0.01) return 3;
    return 4;
  }
}

export { RankingTable };
